import React, { useContext, useEffect, useRef, useState } from "react";
import { CommonContext } from "../../contexts/CommonContext";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import PaymentFailed from "../../assets/payment-failed.png";
import { Capacitor } from "@capacitor/core";
import CashfreePaymentBridge from "../../components/CashfreePaymentBridge";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import Loading from "../../assets/loading.gif";
import { clearPilotCart } from "../../utils/pilotUtils";
import {
  buildPilotInstoreCashfreeOrderDetails,
  roundCashfreeAmount,
} from "../../utils/pilotCashfreeOrder";

const styles = {
  paymentLogo: {
    width: "200px",
  },
};

function PilotMakePayment() {
  const location = useLocation();
  const { hideLoader, isDesktop } = useContext(CommonContext);
  const { getUserId, getCustomerId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [checkPayment, setCheckPayment] = useState(false);
  const navigate = useNavigate();
  const unsubTxn = useRef(null);

  useEffect(() => {
    if (!location.state?.pilotOrderPayload) {
      navigate("/pilot/delivery", { replace: true });
      return;
    }

    const pilotBase = (process.env.REACT_APP_PILOT_URL || "").replace(/\/$/, "");
    if (!pilotBase) {
      console.error("REACT_APP_PILOT_URL is missing");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const pilotOrderPayload = location.state.pilotOrderPayload;
        const userId = await getUserId();
        const customerId = await getCustomerId();
        const orderDetails = buildPilotInstoreCashfreeOrderDetails({
          userId,
          pilotOrderPayload,
        });

        const amount = roundCashfreeAmount(pilotOrderPayload.total);
        const cashfreeBody = {
          amount,
          mobileNo: userId,
          customerId,
          platform: Capacitor.getPlatform(),
          orderDetails,
          version: process.env.REACT_APP_VERSION,
        };

        const res = await fetch(`${pilotBase}/api/payments/cashfree/order`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify(cashfreeBody),
        });
        const data = await res.json();
        if (!res.ok || data.error || !data.order_id || !data.payment_session_id) {
          console.error("Pilot Cashfree session failed:", data);
          setLoading(false);
          return;
        }
        if (cancelled) return;

        unsubTxn.current = onSnapshot(
          doc(db, "transactions", data.order_id),
          async (snap) => {
            const txnData = snap.data();
            if (txnData?.status === "PAYMENT_SUCCESS") {
              if (unsubTxn.current) {
                unsubTxn.current();
                unsubTxn.current = null;
              }
              hideLoader();
              try {
                await clearPilotCart();
              } catch (e) {
                console.warn("clearPilotCart:", e);
              }
              let orderDataParsed;
              try {
                orderDataParsed =
                  typeof txnData.orderData === "string"
                    ? JSON.parse(txnData.orderData)
                    : txnData.orderData;
              } catch (e) {
                orderDataParsed = orderDetails;
              }
              navigate(
                "/orderStatus",
                {
                  state: {
                    orderId: txnData.pilotOrderId,
                    orderData: orderDataParsed,
                  },
                  replace: true,
                }
              );
            } else if (txnData?.status === "PAYMENT_FAILED") {
              setLoading(false);
            }
          }
        );

        const dropConfig = {
          components: [
            "order-details",
            "upi",
            "card",
            "app",
            "netbanking",
            "paylater",
          ],
          onSuccess: () => setCheckPayment(true),
          onFailure: () => setLoading(false),
          style: {
            backgroundColor: "#ffffff",
            color: "#a4243d",
            fontFamily: "Lato",
            fontSize: "14px",
            errorColor: "#ff0000",
            theme: "light",
          },
        };

        if (Capacitor.getPlatform() === "web") {
          const cashfree = new window.Cashfree(data.payment_session_id);
          cashfree.drop(document.getElementById("payment-form"), dropConfig);
        } else {
          await initiatePaymentThroughBridge(
            {
              paymentSessionId: data.payment_session_id,
              orderId: data.order_id,
            },
            pilotBase
          );
        }
      } catch (e) {
        console.error("PilotMakePayment:", e);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubTxn.current) {
        unsubTxn.current();
        unsubTxn.current = null;
      }
    };
  }, []);

  const initiatePaymentThroughBridge = async (data, paymentsBaseUrl) => {
    const base = (paymentsBaseUrl || "").replace(/\/$/, "");
    const resp = await CashfreePaymentBridge.initiatePayment(data);
    if (resp.status === "SUCCESS") {
      await fetch(`${base}/api/payments/cashfree/order-payments`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify({ orderId: data.orderId }),
      })
        .then((r) => r.json())
        .then(() => setCheckPayment(true))
        .catch(() => console.log("Error in processing payment"));
    } else {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        marginTop: "10vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {loading ? (
        <Box>
          {checkPayment ? (
            <Box
              sx={{
                padding: "4vw",
                fontSize: "25px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <img src={Loading} alt="" />
              </Box>
              Checking payment status... Please wait. Do not refresh or close
              the page
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  padding: "4vw",
                  fontSize: "25px",
                  display: "flex",
                  flexDirection: "column",
                  zIndex: 1,
                  textAlign: "center",
                  position: "fixed",
                  left: isDesktop ? "30%" : "10%",
                  top: isDesktop ? "0" : "30%",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <img src={Loading} alt="" />
                </Box>
                Initiating payment... Please wait.
              </Box>
              <Box sx={{ zIndex: 2, position: "relative" }}>
                <div
                  id="payment-form"
                  className={
                    isDesktop ? "gateway-cont-desk" : "gateway-cont-mob"
                  }
                />
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <img src={PaymentFailed} style={styles.paymentLogo} alt="" />
          </Box>
          <h2>Payment Failed</h2>
          <Button
            variant="contained"
            onClick={() => navigate("/pilot/delivery", { replace: true })}
          >
            Back to checkout
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default PilotMakePayment;
